import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Scale, Plus, PenLine } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { DateTimePicker } from '@/components/common/DateTimePicker';
import { useAuth } from '@/features/auth';
import { leoApi, leoKeys } from '@/features/leo';
import { useCourtInstances, useCourtInstanceMutations } from '../hooks';
import { INSTANCE_TYPE_VALUES, DATE_BASIS_VALUES } from '../types';
import type {
  CourtInstance,
  DateBasis,
  InstanceType,
  Matter,
} from '../types';

const NONE = 'NONE';

interface CourtInstancesCardProps {
  matter: Matter;
}

/**
 * Case 360° (Lot 2) Stage 9 — the matter's court-instance ladder. Rendered only
 * for court-case matters (`isCourtCase`). Lists instances (current first) with
 * the court, date basis, and appeal deadline (overdue highlighted); Lead /
 * Partner / MD open and edit instances. Changing a set deadline requires a
 * reason (audited server-side). Courts come from the Stage 8 LEO directory.
 */
export function CourtInstancesCard({ matter }: CourtInstancesCardProps) {
  const { t } = useTranslation();
  const { hasAccess } = useAuth();

  const isCourtCase = !!matter?.isCourtCase;
  const { data: instances, isLoading } = useCourtInstances(
    matter?.id,
    isCourtCase,
  );
  const { createInstanceMutation, updateInstanceMutation } =
    useCourtInstanceMutations();

  // Active LEO courts for the picker (small directory — fetch once).
  const { data: courts } = useQuery({
    queryKey: [...leoKeys.all, 'courts-select'],
    queryFn: () => leoApi.getEntries({ type: 'COURT', limit: 100, page: 0 }),
    enabled: isCourtCase,
    staleTime: 5 * 60 * 1000,
  });

  const canManage =
    hasAccess('matter', 'instance') && matter?.phase !== 'CLOSED';

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<CourtInstance | null>(null);
  const [type, setType] = React.useState<InstanceType>('FIRST');
  const [instanceNumber, setInstanceNumber] = React.useState('1');
  const [dateBasis, setDateBasis] = React.useState<DateBasis>('DECISION_DATE');
  const [baseDate, setBaseDate] = React.useState('');
  const [deadline, setDeadline] = React.useState('');
  const [leoCourtId, setLeoCourtId] = React.useState<string>(NONE);
  const [isCurrent, setIsCurrent] = React.useState(true);
  const [overrideReason, setOverrideReason] = React.useState('');

  if (!isCourtCase) return null;

  const openAdd = () => {
    setEditing(null);
    setType('FIRST');
    setInstanceNumber('1');
    setDateBasis('DECISION_DATE');
    setBaseDate('');
    setDeadline('');
    setLeoCourtId(NONE);
    setIsCurrent(true);
    setOverrideReason('');
    setDialogOpen(true);
  };

  const openEdit = (instance: CourtInstance) => {
    setEditing(instance);
    setType(instance?.type ?? 'FIRST');
    setInstanceNumber(String(instance?.instanceNumber ?? 1));
    setDateBasis(instance?.dateBasis ?? 'DECISION_DATE');
    setBaseDate(
      instance?.baseDate ? format(new Date(instance.baseDate), 'yyyy-MM-dd') : '',
    );
    setDeadline(
      instance?.appealDeadline
        ? format(new Date(instance.appealDeadline), 'yyyy-MM-dd')
        : '',
    );
    setLeoCourtId(instance?.leoCourtId ? String(instance.leoCourtId) : NONE);
    setIsCurrent(instance?.isCurrent ?? true);
    setOverrideReason('');
    setDialogOpen(true);
  };

  // A reason is needed only when an already-set deadline is being changed.
  const originalDeadline = editing?.appealDeadline
    ? format(new Date(editing.appealDeadline), 'yyyy-MM-dd')
    : '';
  const deadlineChanged =
    !!editing && !!originalDeadline && deadline !== originalDeadline;
  const reasonMissing = deadlineChanged && !overrideReason.trim();

  const isPending =
    createInstanceMutation.isPending || updateInstanceMutation.isPending;
  const canSubmit = !isPending && !reasonMissing;

  const handleSubmit = async () => {
    if (!canSubmit || !matter) return;
    const payload = {
      type,
      ...(type === 'REMANDED'
        ? { instanceNumber: Math.max(1, Number(instanceNumber) || 1) }
        : {}),
      dateBasis,
      ...(baseDate ? { baseDate: new Date(baseDate).toISOString() } : {}),
      ...(deadline ? { appealDeadline: new Date(deadline).toISOString() } : {}),
      ...(leoCourtId !== NONE ? { leoCourtId: Number(leoCourtId) } : {}),
      isCurrent,
    };
    if (editing) {
      await updateInstanceMutation.mutateAsync({
        instanceId: editing.id,
        matterId: matter.id,
        data: {
          ...payload,
          ...(deadlineChanged ? { overrideReason: overrideReason.trim() } : {}),
        },
      });
    } else {
      await createInstanceMutation.mutateAsync({ id: matter.id, data: payload });
    }
    setDialogOpen(false);
  };

  const now = Date.now();

  return (
    <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      <CardHeader className="pb-3 border-b border-slate-100">
        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <Scale className="h-5 w-5 text-slate-400" />
            {t('instance.cardTitle')}
          </span>
          {canManage && (
            <Button size="sm" variant="outline" onClick={openAdd}>
              <Plus className="mr-1 h-4 w-4" />
              {t('instance.add')}
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        {isLoading ? (
          <p className="text-sm text-slate-500">{t('loading')}</p>
        ) : !instances?.length ? (
          <p className="text-sm text-slate-500">{t('instance.empty')}</p>
        ) : (
          <ul className="space-y-3 max-h-72 overflow-y-auto pr-1">
            {instances.map((instance) => {
              const overdue =
                !!instance?.appealDeadline &&
                new Date(instance.appealDeadline).getTime() < now &&
                instance?.isCurrent;
              return (
                <li
                  key={instance.id}
                  className={`rounded-md border px-3 py-2 text-sm ${
                    instance?.isCurrent
                      ? 'border-indigo-200 bg-indigo-50/50'
                      : 'border-slate-200 bg-white opacity-70'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2 font-medium text-slate-800">
                      {t(`instance.type.${instance?.type ?? 'FIRST'}`)}
                      {instance?.type === 'REMANDED' && (
                        <span className="text-xs text-slate-500">
                          #{instance?.instanceNumber}
                        </span>
                      )}
                      {instance?.isCurrent && (
                        <Badge className="border border-indigo-300 bg-indigo-50 text-indigo-700">
                          {t('instance.current')}
                        </Badge>
                      )}
                    </span>
                    {canManage && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEdit(instance)}
                      >
                        <PenLine className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                  <div className="mt-1 space-y-0.5 text-xs text-slate-500">
                    {instance?.leoCourt?.name && (
                      <p>
                        {instance.leoCourt.name} — {instance.leoCourt.city}
                      </p>
                    )}
                    <p>
                      {t(`instance.basis.${instance?.dateBasis ?? 'MANUAL'}`)}
                      {instance?.baseDate
                        ? ` · ${format(new Date(instance.baseDate), 'dd MMM yyyy')}`
                        : ''}
                    </p>
                    {instance?.appealDeadline && (
                      <p className={overdue ? 'font-medium text-red-600' : ''}>
                        {t('instance.deadlineLabel')}:{' '}
                        {format(new Date(instance.appealDeadline), 'dd MMM yyyy')}
                        {overdue ? ` — ${t('instance.overdue')}` : ''}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scale className="h-4 w-4 text-slate-500" />
              {editing ? t('instance.editDialogTitle') : t('instance.addDialogTitle')}
            </DialogTitle>
            <DialogDescription>{t('instance.dialogDescription')}</DialogDescription>
          </DialogHeader>

          <div className="my-2 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-2 block text-sm font-medium">
                  {t('instance.typeLabel')}
                </label>
                <Select value={type} onValueChange={(v) => setType(v as InstanceType)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INSTANCE_TYPE_VALUES.map((it) => (
                      <SelectItem key={it} value={it}>
                        {t(`instance.type.${it}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {type === 'REMANDED' && (
                <div>
                  <label className="mb-2 block text-sm font-medium">
                    {t('instance.numberLabel')}
                  </label>
                  <Input
                    type="number"
                    min={1}
                    value={instanceNumber}
                    onChange={(e) => setInstanceNumber(e.target.value)}
                  />
                </div>
              )}
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">
                {t('instance.courtLabel')}
              </label>
              <Select value={leoCourtId} onValueChange={setLeoCourtId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('instance.courtPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('instance.noCourt')}</SelectItem>
                  {(courts?.results ?? [])
                    .filter((c) => !c?.deletedAt)
                    .map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.name} — {c.city}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-2 block text-sm font-medium">
                  {t('instance.basisLabel')}
                </label>
                <Select
                  value={dateBasis}
                  onValueChange={(v) => setDateBasis(v as DateBasis)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_BASIS_VALUES.map((db) => (
                      <SelectItem key={db} value={db}>
                        {t(`instance.basis.${db}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">
                  {t('instance.baseDateLabel')}
                </label>
                <DateTimePicker
                  mode="date"
                  value={baseDate || undefined}
                  onChange={(d) => setBaseDate(d ? format(d, 'yyyy-MM-dd') : '')}
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">
                {t('instance.deadlineLabel')}
              </label>
              <DateTimePicker
                mode="date"
                value={deadline || undefined}
                onChange={(d) => setDeadline(d ? format(d, 'yyyy-MM-dd') : '')}
              />
            </div>

            {deadlineChanged && (
              <div>
                <label className="mb-2 block text-sm font-medium">
                  {t('instance.overrideReasonLabel')}
                </label>
                <Textarea
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder={t('instance.overrideReasonPlaceholder')}
                  rows={2}
                  maxLength={1000}
                />
              </div>
            )}

            <div className="flex items-center gap-2">
              <Switch checked={isCurrent} onCheckedChange={setIsCurrent} />
              <span className="text-sm">{t('instance.isCurrentLabel')}</span>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={isPending}
            >
              {t('cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit}>
              {isPending ? t('processing') : t('instance.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
